import { useEffect, useRef, type ReactNode } from "react"
import styles from "@/styles/components/FloatingBar.module.css"

/**
 * The capsule at the bottom of a running session.
 *
 * Only the shell: a transparent full-width strip that takes NO clicks, holding
 * one centred pill that does. The strip has to be click-through or the round
 * behind it stops being reachable near the bottom of the screen — a swipe deal
 * whose lower third is dead is the bug this shape exists to avoid.
 *
 * What goes in it is `RoundControls`' business. This is here so a second screen
 * needing the same bar cannot reinvent the geometry.
 */

interface FloatingBarProps {
  children: ReactNode
  /**
   * The round's clock has just run out. Shakes the capsule for a beat — the
   * whole pill rather than the clock inside it, because the point is to catch
   * an eye that is somewhere else entirely. See `useTimerAlert`.
   */
  alert?: boolean
}

/* How much of the viewport the bar is eating, published for the rounds that
 * float something directly above it — the vote tray, the write-round pill, the
 * stage's own bottom padding. On a phone the capsule wraps to two or three
 * rows, so its height is a measurement, not a constant. */
const BAR_HEIGHT_VAR = "--floating-bar-height"

export default function FloatingBar({ children, alert = false }: FloatingBarProps) {
  const barRef = useRef<HTMLElement>(null)

  useEffect(() => {
    const bar = barRef.current
    if (!bar) return
    const root = document.documentElement
    // `offsetHeight`, not the entry's content box: the strip's own padding and
    // the phone's safe-area inset are part of what it covers.
    const observer = new ResizeObserver(() => {
      root.style.setProperty(BAR_HEIGHT_VAR, `${bar.offsetHeight}px`)
    })
    observer.observe(bar)
    return () => {
      observer.disconnect()
      // Nothing is floating above a bar that has left, and a stale height would
      // strand the next screen's padding at this round's size.
      root.style.removeProperty(BAR_HEIGHT_VAR)
    }
  }, [])

  return (
    <footer ref={barRef} className={styles.bar}>
      {/* The capsule is what a tooltip inside it squares up to — see `Tooltip`.
       * The bar around it is a click-through strip the full width of the
       * window, so it would bound nothing. */}
      <div
        data-tooltip-boundary
        className={alert ? `${styles.capsule} ${styles.alert}` : styles.capsule}
      >
        {children}
      </div>
    </footer>
  )
}

/** The hairline between two groups inside the bar. */
export function BarDivider() {
  return <span className={styles.divider} aria-hidden="true" />
}
